import React, { FC, useContext } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Stack } from '../stack/Stack';
import { Typography } from '../typography/Typography';
import { TwoColumnContext } from '../../context/TwoColumnContext';

// Interface

export interface QuoteProps {
  text: string;
  quoteName?: string;
  company?: string;
  position?: string;
  centered?: boolean;
}

export interface StyledQuoteProps {
  centered?: boolean;
}

export interface StyledQuoteContainerProps {
  centered?: boolean;
}

export interface StyledStackProps {
  centered?: boolean;
}

// Styles

export const StyledQuote = styled.div<StyledQuoteProps>`
  ${({ theme: { media, colors, space, borderRadii }, centered }) => css`
    display: flex;
    flex-direction: column;
    border-radius: ${borderRadii.medium};
    overflow: hidden;
    align-items: ${centered ? 'center' : 'flex-start'};
    justify-content: ${centered ? 'center' : 'flex-start'};

    ${centered &&
    css`
      /* max-width: 736px; */
      border-top: 1px solid ${colors.brandOne20};
      border-bottom: 1px solid ${colors.brandOne20};
      margin-left: auto;
      margin-right: auto;
      padding: ${space.xxLarge} 0;
    `}

    @media (min-width: ${media.medium}) {
      flex-direction: row;
    }
  `};
`;

const StyledQuoteContainer = styled.div<StyledQuoteContainerProps>`
  ${({ theme: { space }, centered }) => css`
    padding: ${centered
      ? '0'
      : `0 ${space.fluidMedium} ${space.fluidMedium} 0`};
    display: flex;
    flex-direction: column;
  `};
`;

const StyledStack = styled(Stack)<StyledStackProps>`
  ${({ centered }) => css`
    display: flex;
    flex-direction: column;
    align-items: ${centered ? 'center' : 'flex-start'};

    p {
      text-align: ${centered ? 'center' : 'left'};
    }
  `};
`;

// JSX

export const Quote: FC<QuoteProps> = ({
  text,
  quoteName,
  position,
  company,
  centered,
}) => {
  const isInTwoColumnLayout = useContext(TwoColumnContext);

  return (
    <StyledQuote centered={!isInTwoColumnLayout}>
      <StyledQuoteContainer centered={!isInTwoColumnLayout}>
        <StyledStack gap="large" centered={!isInTwoColumnLayout}>
          {isInTwoColumnLayout ? (
            <Typography variant="quote">“{text}”</Typography>
          ) : (
            <Typography variant="headingLarge">“{text}”</Typography>
          )}
          <div>
            {quoteName && (
              <Typography variant="bodyMediumBold">{quoteName}</Typography>
            )}
            {(position || company) && (
              <Typography variant="bodyMedium">
                {company && `${company}`}
                {company && position && ' / '}
                {position && `${position}`}
              </Typography>
            )}
          </div>
        </StyledStack>
      </StyledQuoteContainer>
    </StyledQuote>
  );
};

export default Quote;
